import React, { createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [vaultTab, setVaultTab] = React.useState('photo'); // 'photo' | 'certificates' | 'documents' | 'portfolio'
  const [certificates, setCertificates] = React.useState([
    { id: 1, title: 'AWS Certified Cloud Practitioner', issuer: 'Amazon Web Services', credentialUrl: 'https://aws.amazon.com', issueDate: '2025-01-10', expiryDate: '2028-01-10', pdfName: 'aws_cert.pdf' }
  ]);
  const [portfolioAssets, setPortfolioAssets] = React.useState([
    { label: 'GitHub', url: 'https://github.com/arjunsharma' },
    { label: 'Portfolio', url: 'https://arjunsharma.dev' }
  ]);
  const [showCustomize, setShowCustomize] = React.useState(false);

  const {
    theme, toggleTheme,
    mobileTab, setMobileTab,
    showMobileActions, setShowMobileActions,
    showHistorySidebar, setShowHistorySidebar,
    authToken, user, setUser, handleLogout,
    credits, setCredits, setPlan,
    showPricing, setShowPricing,
    aiTone, setAiTone,
  } = useAppContext();

  // Derive activeTab from pathname and search queries
  const path = location.pathname;
  let activeTab = 'home';
  if (path === '/app/dashboard') {
    activeTab = 'home';
  } else if (path.startsWith('/app/vault')) {
    activeTab = 'assets_vault';
  } else if (path.startsWith('/app/tracker')) {
    activeTab = 'tracker';
  } else if (path.startsWith('/app/profile')) {
    activeTab = 'profile';
  } else if (path.startsWith('/app/cover-letter')) {
    const queryParams = new URLSearchParams(location.search);
    const subTab = queryParams.get('tab');
    if (subTab && ['templates', 'editor', 'chat'].includes(subTab)) {
      activeTab = subTab;
    } else {
      activeTab = 'chat'; // default to chat
    }
  } else if (path.startsWith('/app/resume')) {
    const queryParams = new URLSearchParams(location.search);
    const subTab = queryParams.get('tab');
    if (subTab && ['ats', 'jd', 'jd_match', 'mock_interview', 'career_roadmap', 'audit', 'templates'].includes(subTab)) {
      activeTab = (subTab === 'jd_match' || subTab === 'jd') ? 'jd' : subTab;
    } else if (path.endsWith('/editor')) {
      activeTab = 'editor';
    } else {
      activeTab = 'chat'; // default to chat
    }
  }

  const getResumeIdFromPath = (pathname) => {
    const parts = pathname.split('/');
    const resumeIndex = parts.indexOf('resume');
    if (resumeIndex !== -1 && parts[resumeIndex + 1]) {
      return parts[resumeIndex + 1];
    }
    return 'master';
  };

  const getCoverLetterIdFromPath = (pathname) => {
    const parts = pathname.split('/');
    const clIndex = parts.indexOf('cover-letter');
    if (clIndex !== -1 && parts[clIndex + 1]) {
      return parts[clIndex + 1];
    }
    return null;
  };

  const setActiveTab = (tab) => {
    const isCL = location.pathname.startsWith('/app/cover-letter');
    if (tab === 'home') {
      navigate('/app/dashboard');
    } else if (tab === 'assets_vault') {
      navigate('/app/vault');
    } else if (tab === 'tracker') {
      navigate('/app/tracker');
    } else if (tab === 'profile') {
      navigate('/app/profile');
    } else if (isCL) {
      const clId = getCoverLetterIdFromPath(location.pathname);
      const tabVal = tab === 'ai' ? 'chat' : tab;
      if (tabVal === 'chat') {
        navigate(`/app/cover-letter/${clId}`);
      } else {
        navigate(`/app/cover-letter/${clId}?tab=${tabVal}`);
      }
    } else if (['ats', 'jd', 'templates', 'mock_interview', 'career_roadmap', 'audit'].includes(tab)) {
      const resId = getResumeIdFromPath(location.pathname);
      const tabVal = tab === 'jd' ? 'jd' : tab;
      navigate(`/app/resume/${resId}/chat?tab=${tabVal}`);
    } else if (tab === 'chat' || tab === 'ai') {
      const resId = getResumeIdFromPath(location.pathname);
      navigate(`/app/resume/${resId}/chat`);
    } else if (tab === 'editor') {
      const resId = getResumeIdFromPath(location.pathname);
      navigate(`/app/resume/${resId}/editor`);
    }
  };

  const value = {
    activeTab,
    setActiveTab,
    theme,
    toggleTheme,
    mobileTab,
    setMobileTab,
    showMobileActions,
    setShowMobileActions,
    showHistorySidebar,
    setShowHistorySidebar,
    authToken,
    user,
    setUser,
    handleLogout,
    credits,
    setCredits,
    setPlan,
    vaultTab,
    setVaultTab,
    certificates,
    setCertificates,
    portfolioAssets,
    setPortfolioAssets,
    showPricing,
    setShowPricing,
    showCustomize,
    setShowCustomize,
    aiTone,
    setAiTone,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaceContext() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceProvider');
  }
  return context;
}
